import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { CalendarDays, ChevronDown, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { GeneratePanel } from "./GeneratePanel";
import { LessonList } from "./LessonList";
import { LessonDialog } from "./LessonDialog";
import { LockControls } from "./LockControls";
import { teacherOverlaps, canEditCourse } from "./calendarUtils";

export const CourseCalendar = ({ year, course, onMeta }) => {
  const { user } = useAuth();
  const isMaster = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // { lessons, closures, meta }
  const [dlg, setDlg] = useState(null); // { lesson } | { lesson: null } per recupero

  const load = async () => {
    try {
      const [l, c, m] = await Promise.all([
        api.get("/staff/lessons", { params: { year, course } }),
        api.get("/staff/closures", { params: { year } }),
        api.get("/staff/course-meta", { params: { year } }),
      ]);
      setState({ lessons: l.data, closures: c.data, meta: m.data.find((x) => x.course === course) || null });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggle = (o) => { setOpen(o); if (o && !state) load(); };

  const sortL = (ls) => [...ls].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const replace = (x) => setState((s) => ({ ...s, lessons: sortL(s.lessons.map((y) => (y.id === x.id ? { ...x, teacher_name: y.teacher_name } : y))) }));
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); } catch (err) { toast.error(apiError(err.response?.data?.detail)); throw err; } };
  const setMeta = (data) => { setState((s) => ({ ...s, meta: data })); onMeta?.(data); };

  const actions = {
    move: (x) => setDlg({ lesson: x }),
    cancel: (x) => call(async () => replace((await api.post(`/staff/lessons/${x.id}/cancel`)).data), "Lezione annullata").catch(() => {}),
    restore: (x) => call(async () => replace((await api.post(`/staff/lessons/${x.id}/restore`)).data), "Lezione ripristinata").catch(() => {}),
    remove: (x) => call(async () => { await api.delete(`/staff/lessons/${x.id}`); setState((s) => ({ ...s, lessons: s.lessons.filter((y) => y.id !== x.id) })); }, "Recupero eliminato").catch(() => {}),
  };
  const submitDlg = (v) => dlg.lesson
    ? call(async () => replace((await api.post(`/staff/lessons/${dlg.lesson.id}/move`, v)).data), "Lezione spostata")
    : call(async () => { const { data } = await api.post("/staff/lessons", { course, ...v }, { params: { year } }); setState((s) => ({ ...s, lessons: sortL([...s.lessons, { ...data, teacher_name: s.meta?.teacher_name || "" }]) })); }, "Recupero aggiunto");

  const active = state ? state.lessons.filter((x) => x.status !== "cancelled").length : 0;
  const editable = canEditCourse(state?.meta, user);
  const isOwner = !!state?.meta?.teacher_user_id && state.meta.teacher_user_id === user?.user_id;
  const setVisible = (v) => call(async () => {
    setMeta((await api.put("/admin/course-meta/visibility", { course, visible: v }, { params: { year } })).data);
  }, v ? "Calendario visibile agli allievi" : "Calendario nascosto agli allievi").catch(() => {});
  const lock = () => call(async () => setMeta((await api.post("/staff/course-lock", { course }, { params: { year } })).data), "Calendario confermato e bloccato").catch(() => {});
  const unlock = () => call(async () => setMeta((await api.post("/admin/course-unlock", { course }, { params: { year } })).data), "Calendario sbloccato").catch(() => {});

  return (
    <Card className="bg-card border-border mb-5" data-testid="course-calendar">
      <Collapsible open={open} onOpenChange={toggle}>
        <CollapsibleTrigger data-testid="course-calendar-toggle" className="w-full p-5 flex items-center justify-between gap-2 text-left">
          <span className="text-lg flex items-center gap-2"><CalendarDays size={18} className="text-primary" /> Calendario lezioni
            {state?.meta?.calendar_locked && <Lock size={15} className="text-foreground" data-testid="course-calendar-locked-icon" />}</span>
          <ChevronDown size={18} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-5 pb-5 space-y-4">
          {!state ? <p className="text-sm text-muted-foreground">Caricamento…</p> : (
            <>
              <p className="text-sm text-muted-foreground">Docente: <span className="text-foreground">{state.meta?.teacher_name || "non indicato"}</span> · {active} lezioni in calendario</p>
              <LockControls meta={state.meta} isMaster={isMaster} isOwner={isOwner} onLock={lock} onUnlock={unlock} />
              {isMaster && (
                <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 cursor-pointer" data-testid="course-visibility-row">
                  <span className="text-sm"><span className="font-medium text-foreground">Visibile agli allievi</span><span className="block text-muted-foreground">{state.meta?.student_visible ? "Gli iscritti a questo corso vedono il calendario nella loro area." : "Spento: gli allievi non vedono il calendario di questo corso."}</span></span>
                  <Switch data-testid="course-visibility-switch" checked={!!state.meta?.student_visible} onCheckedChange={setVisible} />
                </label>
              )}
              {editable && <GeneratePanel key={state.meta?.recital_date || "none"} year={year} course={course} meta={state.meta} onGenerated={load} />}
              {editable && <div className="flex justify-end"><Button size="sm" variant="outline" data-testid="lesson-add-recovery" onClick={() => setDlg({ lesson: null })}><Plus size={14} className="mr-1" /> Lezione di recupero</Button></div>}
              {state.lessons.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="lessons-empty">Nessuna lezione ancora.{editable ? " Genera il calendario qui sopra." : ""}</p>
                : <LessonList lessons={state.lessons} overlaps={teacherOverlaps(state.lessons)} actions={editable ? actions : null} />}
              <LessonDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} lesson={dlg?.lesson || null} closures={state.closures} onSubmit={submitDlg} />
            </>
          )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
